import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Plus, Edit, Trash2, Save, X, Search, Building2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

export default function CompanyMaster() {
  const { user } = useAuth();
  const canManage = !!user;
  const [list, setList] = useState([]);
  const [edit, setEdit] = useState(null);
  const [q, setQ] = useState("");

  const blank = { name: "", address: "", gst: "" };

  const load = async () => {
    const r = await api.get("/companies-book");
    setList(r.data || []);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!edit?.name?.trim()) return toast.error("Company name is required");
    try {
      if (edit.id) {
        await api.put(`/companies-book/${edit.id}`, edit);
        toast.success("Company updated");
      } else {
        await api.post("/companies-book", edit);
        toast.success("Company added");
      }
      setEdit(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this company entry?")) return;
    try {
      await api.delete(`/companies-book/${id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return list;
    return list.filter((c) =>
      [c.name, c.address, c.gst].some((v) => (v || "").toLowerCase().includes(s))
    );
  }, [list, q]);

  const inputCls = "w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1D2951]/20 focus:border-[#1D2951]";

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <Building2 size={18} className="text-[#1D2951]" />
            <h1 className="text-2xl font-bold text-slate-900">Company Master</h1>
          </div>
          <p className="text-xs text-slate-500 mt-1">Manage subsidiary / sister-company records used across indents and the dashboard.</p>
        </div>
        {canManage && (
          <button
            onClick={() => setEdit({ ...blank })}
            data-testid="company-add-btn"
            className="bg-[#1D2951] hover:bg-[#2B3A67] text-white px-4 py-2 rounded-md text-sm font-semibold inline-flex items-center gap-2"
          >
            <Plus size={14} /> Add Company
          </button>
        )}
      </div>

      {edit && (
        <div className="bg-white border border-slate-200 rounded-lg p-5 space-y-3">
          <div className="text-sm font-semibold text-slate-900">{edit.id ? "Edit Company" : "New Company"}</div>
          <input data-testid="company-name-input" value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} placeholder="Company Name" className={inputCls} />
          <input data-testid="company-address-input" value={edit.address || ""} onChange={(e) => setEdit({ ...edit, address: e.target.value })} placeholder="Address" className={inputCls} />
          <input data-testid="company-gst-input" value={edit.gst || ""} onChange={(e) => setEdit({ ...edit, gst: e.target.value })} placeholder="GST Number" className={inputCls} />
          <div className="flex items-center justify-end gap-2">
            <button onClick={() => setEdit(null)} className="px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100 rounded-md inline-flex items-center gap-2"><X size={14} /> Cancel</button>
            <button onClick={save} data-testid="company-save-btn" className="bg-[#1D2951] hover:bg-[#2B3A67] text-white px-4 py-1.5 rounded-md text-sm font-semibold inline-flex items-center gap-2"><Save size={14} /> Save</button>
          </div>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="px-6 py-3 border-b border-slate-100 flex items-center justify-between gap-3">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">All Companies</div>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              data-testid="company-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name, address or GST…"
              className="pl-9 pr-3 py-1.5 border border-slate-300 rounded-md text-sm w-80 focus:outline-none focus:ring-2 focus:ring-[#1D2951]/20 focus:border-[#1D2951]"
            />
          </div>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600">
              <th className="text-left px-6 py-3">Name</th>
              <th className="text-left px-6 py-3">Address</th>
              <th className="text-left px-6 py-3">GST</th>
              {canManage && <th className="text-right px-6 py-3">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan="4" className="text-center py-12 text-slate-400">{list.length === 0 ? "No companies yet" : "No matches"}</td></tr>
            )}
            {filtered.map((c) => (
              <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-6 py-3 font-semibold text-slate-900">{c.name}</td>
                <td className="px-6 py-3 text-slate-700">{c.address || "—"}</td>
                <td className="px-6 py-3 font-mono text-xs">{c.gst || "—"}</td>
                {canManage && (
                  <td className="px-6 py-3 text-right">
                    <button onClick={() => setEdit({ id: c.id, name: c.name, address: c.address || "", gst: c.gst || "" })} className="text-[#1D2951] hover:underline mr-4 inline-flex items-center gap-1 text-xs font-semibold"><Edit size={12} /> Edit</button>
                    <button onClick={() => remove(c.id)} className="text-rose-600 hover:underline inline-flex items-center gap-1 text-xs font-semibold"><Trash2 size={12} /> Delete</button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
